/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  isToday,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import Breadcrumb from "../../components/Breadcrumb";
import makeRequest from "../../makeRequest";
import { PatientAuthContext } from "../../contexts/PatientAuthContext";
import { patientApi } from "../../utils/patientApi";
import config from "../../config";

/* ─── Injected styles ─────────────────────────────────────────────── */
const STYLE_ID = "appt-page-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes apptSpin { to { transform: rotate(360deg); } }
    .appt-spinner {
      width: 44px; height: 44px;
      border: 4px solid #e2ecf9; border-top-color: #1b74bb;
      border-radius: 50%; animation: apptSpin .8s linear infinite; margin: 0 auto;
    }
    .appt-steps {
      display: flex; align-items: flex-start; justify-content: center;
      margin-bottom: 48px; flex-wrap: wrap;
    }
    .appt-step {
      display: flex; flex-direction: column; align-items: center; gap: 6px;
      position: relative; flex: 1; max-width: 180px;
    }
    .appt-step::before {
      content: ''; position: absolute; top: 20px; left: 50%;
      width: 100%; height: 2px; background: #d1dff0; z-index: 0;
    }
    .appt-step:last-child::before { display: none; }
    .appt-step__dot {
      width: 40px; height: 40px; border-radius: 50%;
      background: #e8f1fb; border: 2px solid #c4d8f0;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700; color: #7ba7cd;
      position: relative; z-index: 1; transition: all .3s;
    }
    .appt-step.active .appt-step__dot {
      background: #1b74bb; border-color: #1b74bb; color: #fff;
      box-shadow: 0 4px 16px rgba(27,116,187,0.35);
    }
    .appt-step.done .appt-step__dot { background: #22c55e; border-color: #22c55e; color: #fff; }
    .appt-step__label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; }
    .appt-step.active .appt-step__label { color: #1b74bb; }
    .appt-step.done .appt-step__label { color: #22c55e; }

    .appt-search {
      background: #fff; border-radius: 16px; padding: 28px 32px;
      box-shadow: 0 4px 24px rgba(27,116,187,0.08); border: 1px solid #dce8f5; margin-bottom: 36px;
    }
    @media(max-width:767px){ .appt-search { padding: 20px 16px; } }
    .appt-search__row { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-end; }
    .appt-search__field { flex: 1; min-width: 180px; }
    .appt-search__field label {
      display: block; font-size: 11px; font-weight: 700; letter-spacing: 1.5px;
      text-transform: uppercase; color: #64748b; margin-bottom: 8px;
    }
    .appt-search__input {
      width: 100%; border: 1.5px solid #e2ecf9; border-radius: 10px;
      padding: 11px 16px; font-size: 14px; color: #0f172a; background: #f8fafc;
      outline: none; transition: border-color .2s, box-shadow .2s;
    }
    .appt-search__input:focus {
      border-color: #1b74bb; box-shadow: 0 0 0 3px rgba(27,116,187,0.1); background: #fff;
    }
    .appt-search__btn {
      background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%);
      color: #fff; border: none; border-radius: 10px; padding: 12px 28px;
      font-size: 14px; font-weight: 700; cursor: pointer; white-space: nowrap;
      transition: transform .2s, box-shadow .2s; display: inline-flex; align-items: center; gap: 8px;
    }
    .appt-search__btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(27,116,187,0.3); }

    .appt-doc-card {
      background: #fff; border-radius: 16px; overflow: hidden;
      border: 1.5px solid #e8f1fb; box-shadow: 0 4px 20px rgba(27,116,187,0.07);
      transition: transform .25s, box-shadow .25s, border-color .25s;
      height: 100%; display: flex; flex-direction: column;
    }
    .appt-doc-card:hover { transform: translateY(-6px); border-color: #1b74bb; box-shadow: 0 16px 48px rgba(27,116,187,0.18); }
    .appt-doc-card__photo {
      position: relative; width: 100%; height: 200px; overflow: hidden;
      background: linear-gradient(145deg,#e8f1fb 0%,#dce8f5 100%);
    }
    .appt-doc-card__photo img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .4s; }
    .appt-doc-card:hover .appt-doc-card__photo img { transform: scale(1.05); }
    .appt-doc-card__photo-placeholder {
      width: 100%; height: 100%; display: flex; flex-direction: column;
      align-items: center; justify-content: center; gap: 10px;
      background: linear-gradient(145deg,#e8f1fb 0%,#c8dcf0 100%);
    }
    .appt-doc-card__photo-placeholder i { font-size: 56px; color: #93b9d9; }
    .appt-doc-card__speciality-badge {
      position: absolute; top: 10px; right: 10px;
      background: rgba(27,116,187,0.9); color: #fff;
      font-size: 10px; font-weight: 700; letter-spacing: 1px;
      text-transform: uppercase; padding: 4px 10px; border-radius: 20px;
    }
    .appt-doc-card__body { padding: 20px; flex: 1; display: flex; flex-direction: column; }
    .appt-doc-card__name { font-size: 16px; font-weight: 800; color: #0f172a; margin: 0 0 4px; line-height: 1.3; }
    .appt-doc-card__desig { font-size: 12px; color: #1b74bb; font-weight: 600; margin-bottom: 4px; }
    .appt-doc-card__spec { font-size: 12px; color: #64748b; margin-bottom: 16px; flex: 1; display: flex; align-items: center; gap: 6px; }
    .appt-doc-card__btn {
      width: 100%; background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%);
      color: #fff; border: none; border-radius: 10px; padding: 11px;
      font-size: 13px; font-weight: 700; cursor: pointer;
      transition: opacity .2s, transform .2s; display: flex; align-items: center; justify-content: center; gap: 8px;
    }
    .appt-doc-card__btn:hover { opacity: .9; transform: translateY(-1px); }

    .appt-pagination { display: flex; gap: 6px; justify-content: center; margin-top: 32px; flex-wrap: wrap; }
    .appt-page-btn {
      width: 36px; height: 36px; border-radius: 8px; border: 1.5px solid #dce8f5;
      background: #fff; color: #1b74bb; font-size: 13px; font-weight: 600;
      cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .2s;
    }
    .appt-page-btn:hover, .appt-page-btn.active { background: #1b74bb; color: #fff; border-color: #1b74bb; }
    .appt-page-btn:disabled { opacity: .4; cursor: not-allowed; }

    .appt-booking-grid { display: grid; grid-template-columns: 340px 1fr; gap: 28px; align-items: start; }
    @media(max-width:991px){ .appt-booking-grid { grid-template-columns: 1fr; } }

    .appt-doc-summary {
      background: #fff; border-radius: 16px; padding: 20px;
      border: 1.5px solid #e8f1fb; box-shadow: 0 4px 20px rgba(27,116,187,0.08);
      display: flex; gap: 16px; align-items: center; margin-bottom: 20px;
    }
    .appt-doc-summary__photo {
      width: 72px; height: 72px; border-radius: 50%; overflow: hidden;
      background: linear-gradient(145deg,#e8f1fb,#c8dcf0); flex-shrink: 0; border: 3px solid #e2ecf9;
      display: flex; align-items: center; justify-content: center;
    }
    .appt-doc-summary__photo img { width: 100%; height: 100%; object-fit: cover; display: block; }

    .appt-calendar { background: #fff; border-radius: 16px; overflow: hidden; border: 1.5px solid #e8f1fb; box-shadow: 0 4px 20px rgba(27,116,187,0.08); }
    .appt-calendar__header {
      background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%);
      padding: 18px 20px; display: flex; align-items: center; justify-content: space-between;
    }
    .appt-calendar__header h3 { color: #fff; font-size: 18px; font-weight: 800; margin: 0; }
    .appt-cal-nav {
      width: 36px; height: 36px; border-radius: 8px; background: rgba(255,255,255,0.15);
      border: none; color: #fff; font-size: 18px; cursor: pointer;
      display: flex; align-items: center; justify-content: center; transition: background .2s;
    }
    .appt-cal-nav:hover:not(:disabled) { background: rgba(255,255,255,0.28); }
    .appt-cal-nav:disabled { opacity: .35; cursor: not-allowed; }
    .appt-calendar__days-header {
      display: grid; grid-template-columns: repeat(7,1fr);
      background: #f0f6fb; padding: 10px 12px 8px;
    }
    .appt-calendar__days-header span { text-align: center; font-size: 11px; font-weight: 700; letter-spacing: 1px; color: #7ba7cd; text-transform: uppercase; }
    .appt-calendar__grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 4px; padding: 12px; }
    .appt-cal-day {
      aspect-ratio: 1; border-radius: 8px; border: none; background: transparent;
      font-size: 13px; font-weight: 600; color: #0f172a; cursor: pointer;
      display: flex; align-items: center; justify-content: center; transition: all .2s; position: relative;
    }
    .appt-cal-day:hover:not(:disabled) { background: #edf5fb; color: #1b74bb; }
    .appt-cal-day.today { color: #1b74bb; font-weight: 800; }
    .appt-cal-day.today::after { content: ''; position: absolute; bottom: 3px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; background: #1b74bb; }
    .appt-cal-day.selected { background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%); color: #fff !important; box-shadow: 0 4px 12px rgba(27,116,187,0.35); }
    .appt-cal-day.selected::after { background: #fff; }
    .appt-cal-day:disabled { color: #c4d0db; cursor: not-allowed; background: transparent; }
    .appt-cal-day.empty { pointer-events: none; }

    .appt-slots-panel { background: #fff; border-radius: 16px; padding: 24px; border: 1.5px solid #e8f1fb; box-shadow: 0 4px 20px rgba(27,116,187,0.08); }
    .appt-slots-panel h4 { font-size: 16px; font-weight: 800; color: #0f172a; margin: 0 0 16px; }
    .appt-slots-grid { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 20px; }
    .appt-slot-btn {
      border: 1.5px solid #dce8f5; border-radius: 8px; background: #f8fafc;
      color: #0f172a; font-size: 13px; font-weight: 600; padding: 8px 14px;
      cursor: pointer; transition: all .2s; display: flex; align-items: center; gap: 6px;
    }
    .appt-slot-btn:hover { border-color: #1b74bb; color: #1b74bb; background: #edf5fb; }
    .appt-slot-btn.selected { background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%); color: #fff; border-color: #1b74bb; box-shadow: 0 4px 12px rgba(27,116,187,0.3); }

    .appt-confirm-bar {
      background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%);
      border-radius: 12px; padding: 16px 20px; margin-top: 16px;
      display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    }
    .appt-confirm-bar span { color: rgba(255,255,255,0.85); font-size: 13px; font-weight: 500; }
    .appt-confirm-bar strong { color: #fff; display: block; font-size: 14px; margin-top: 2px; }
    .appt-confirm-btn {
      background: #fff; color: #1b74bb; border: none; border-radius: 8px;
      padding: 10px 24px; font-size: 14px; font-weight: 800; cursor: pointer;
      transition: transform .2s, box-shadow .2s; white-space: nowrap; display: inline-flex; align-items: center; gap: 8px;
    }
    .appt-confirm-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.15); }
    .appt-confirm-btn:disabled { opacity: .6; cursor: not-allowed; }

    @keyframes apptBounce { 0%,100%{ transform:scale(1); } 50%{ transform:scale(1.12); } }
    .appt-success { text-align: center; padding: 80px 24px; }
    .appt-success__icon {
      width: 96px; height: 96px; border-radius: 50%;
      background: linear-gradient(135deg,#22c55e,#16a34a);
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 28px; animation: apptBounce 1.2s ease 1;
      box-shadow: 0 12px 48px rgba(34,197,94,0.35);
    }
    .appt-success__icon i { font-size: 46px; color: #fff; }
    .appt-success h2 { font-size: clamp(24px,3vw,36px); font-weight: 900; color: #0f172a; margin: 0 0 12px; }
    .appt-success p { font-size: 15px; color: #64748b; max-width: 480px; margin: 0 auto 32px; line-height: 1.7; }
    .appt-back-btn {
      display: inline-flex; align-items: center; gap: 8px;
      background: none; border: 1.5px solid #1b74bb; color: #1b74bb;
      border-radius: 10px; padding: 11px 24px; font-size: 14px; font-weight: 700;
      cursor: pointer; transition: all .2s;
    }
    .appt-back-btn:hover { background: #1b74bb; color: #fff; }
    .appt-section-label {
      display: inline-flex; align-items: center; gap: 8px;
      background: #edf5fb; border: 1.5px solid #bee0ef;
      border-radius: 50px; padding: 6px 20px; font-size: 11px; font-weight: 700;
      letter-spacing: 2px; text-transform: uppercase; color: #1b74bb;
    }

    /* ── Patient identity modal ── */
    .appt-modal-overlay {
      position: fixed; inset: 0; z-index: 9000;
      background: rgba(10,30,60,0.55); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center; padding: 16px;
    }
    .appt-modal {
      background: #fff; border-radius: 20px; width: 100%; max-width: 460px;
      box-shadow: 0 24px 80px rgba(0,0,0,0.22); overflow: hidden;
      animation: apptModalIn .25s ease;
    }
    @keyframes apptModalIn { from { opacity: 0; transform: translateY(20px) scale(.97); } to { opacity: 1; transform: none; } }
    .appt-modal__header {
      background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%);
      padding: 20px 24px; display: flex; align-items: center; justify-content: space-between;
    }
    .appt-modal__header h3 { color: #fff; font-size: 17px; font-weight: 800; margin: 0; display: flex; align-items: center; gap: 10px; }
    .appt-modal__close {
      width: 32px; height: 32px; border-radius: 8px; border: none;
      background: rgba(255,255,255,0.18); color: #fff; font-size: 16px;
      cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .2s;
    }
    .appt-modal__close:hover { background: rgba(255,255,255,0.32); }
    .appt-modal__body { padding: 24px; }
    .appt-modal__tabs { display: flex; gap: 8px; margin-bottom: 20px; }
    .appt-modal__tab {
      flex: 1; padding: 9px 12px; border-radius: 10px; border: 1.5px solid #dce8f5;
      background: #f8fafc; font-size: 13px; font-weight: 700; color: #64748b;
      cursor: pointer; transition: all .2s; text-align: center;
    }
    .appt-modal__tab.active { background: #1b74bb; color: #fff; border-color: #1b74bb; }
    .appt-modal__input {
      width: 100%; border: 1.5px solid #dce8f5; border-radius: 10px;
      padding: 11px 14px; font-size: 14px; color: #0f172a; background: #f8fafc;
      outline: none; box-sizing: border-box; transition: border-color .2s;
    }
    .appt-modal__input:focus { border-color: #1b74bb; background: #fff; }
    .appt-modal__btn {
      width: 100%; background: linear-gradient(135deg,#1b74bb 0%,#0d3b6e 100%);
      color: #fff; border: none; border-radius: 10px; padding: 13px;
      font-size: 14px; font-weight: 800; cursor: pointer; margin-top: 14px;
      display: flex; align-items: center; justify-content: center; gap: 8px; transition: opacity .2s;
    }
    .appt-modal__btn:disabled { opacity: .6; cursor: not-allowed; }
    .appt-modal__error {
      background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;
      padding: 9px 12px; font-size: 13px; color: #dc2626; margin-bottom: 12px;
      display: flex; gap: 8px; align-items: center;
    }
    .appt-modal__info {
      background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px;
      padding: 10px 13px; font-size: 13px; color: #0369a1; margin-bottom: 12px;
    }
    .appt-modal__phone-btn {
      width: 100%; background: #f0f6ff; color: #0d3b6e; border: 1.5px solid #1b74bb;
      border-radius: 9px; padding: 10px 14px; font-size: 13px; font-weight: 700;
      cursor: pointer; text-align: left; margin-bottom: 7px; transition: all .2s;
      display: flex; align-items: center; gap: 8px;
    }
    .appt-modal__phone-btn:hover { background: #1b74bb; color: #fff; }
    .appt-modal__link { background: none; border: none; color: #94a3b8; font-size: 12px; cursor: pointer; padding: 0; margin-top: 8px; display: inline-block; }
    .appt-modal__link:hover { color: #1b74bb; }
    .appt-modal__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .appt-modal__booking-summary {
      background: linear-gradient(135deg,#f0f7ff,#e0eefb); border-radius: 10px;
      padding: 11px 14px; margin-bottom: 14px; font-size: 13px;
      border: 1px solid #bee0ef;
    }
    .appt-modal__booking-summary strong { color: #0d3b6e; display: block; font-size: 14px; font-weight: 800; margin-bottom: 2px; }
    .appt-modal__booking-summary span { color: #374151; }
  `;
  document.head.appendChild(s);
}

/* ─── Constants ─────────────────────────────────────────────────── */
const BASE_URL = config.api.baseUrl;
const DOCS_PER_PAGE = 9;
const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* ─── Parse Oracle time string "05-MAY-26 09:00:00" → "9:00 AM" ─── */
const parseSlotTime = (timeSlot) => {
  try {
    const timePart = timeSlot.split(" ")[1];
    if (!timePart) return timeSlot;
    const [h, m] = timePart.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  } catch {
    return timeSlot;
  }
};

/* ─── DoctorImage with fallback ─────────────────────────────────── */
function DoctorImage({ src, name, style }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="appt-doc-card__photo-placeholder" style={style}>
        <i className="fa-solid fa-user-doctor" />
      </div>
    );
  }
  return <img src={src} alt={name} style={style} onError={() => setFailed(true)} />;
}

/* ═══ Main Component ═══════════════════════════════════════════════ */
const AppointmentMain = () => {
  const navigate = useNavigate();
  const { patient, login, isLoading: authLoading } = useContext(PatientAuthContext);

  /* ── Steps ── */
  const [step, setStep] = useState("doctors"); // "doctors" | "booking" | "success"

  /* ── Doctor list ── */
  const [doctors, setDoctors] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchName, setSearchName] = useState("");
  const [searchSpeciality, setSearchSpeciality] = useState("");
  const [specialities, setSpecialities] = useState([]);
  const searchTimerRef = useRef(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [websiteDocMap, setWebsiteDocMap] = useState({}); // skmId → { id, hasImage, designation }

  /* ── Booking ── */
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookedMrno, setBookedMrno] = useState(null);
  const slotsRef = useRef(null);

  /* ── Patient identity modal ── */
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [modalTab, setModalTab] = useState("existing");
  /* existing-patient OTP */
  const [modalMrno, setModalMrno] = useState("");
  const [modalOtpStep, setModalOtpStep] = useState("mrno"); // "mrno"|"select_phone"|"otp_pending"
  const [modalLookupResult, setModalLookupResult] = useState(null);
  const [modalSelectedPhone, setModalSelectedPhone] = useState("");
  const [modalOtp, setModalOtp] = useState("");
  const [modalOtpInfo, setModalOtpInfo] = useState(null);
  const [modalOtpLoading, setModalOtpLoading] = useState(false);
  const [modalOtpError, setModalOtpError] = useState("");
  /* new-patient form */
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newCnic, setNewCnic] = useState("");
  const [newDob, setNewDob] = useState("");
  const [newGender, setNewGender] = useState("1");
  /* new-patient OTP */
  const [newPatientStep, setNewPatientStep] = useState("form"); // "form"|"otp_pending"
  const [newTempMrno, setNewTempMrno] = useState("");
  const [newOtp, setNewOtp] = useState("");
  const [newOtpInfo, setNewOtpInfo] = useState(null);
  const [newRegLoading, setNewRegLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  /* ── Load website doctor map (skmId → {id,hasImage,designation}) ── */
  useEffect(() => {
    makeRequest({ route: "/website/doctor?limit=500&page=1", method: "get" })
      .then((res) => {
        const map = {};
        (res.data || []).forEach((d) => {
          if (d.skmId) map[String(d.skmId)] = d;
        });
        setWebsiteDocMap(map);
      })
      .catch(() => {});
  }, []);

  /* ── Load specialities ── */
  useEffect(() => {
    makeRequest({ route: "/taleeb/speciality?limit=200&page=1", method: "get" })
      .then((res) => setSpecialities((res?.data?.specialities?.data || []).map((s) => s.SPECIALITY).filter(Boolean)))
      .catch(() => {});
  }, []);

  /* ── Fetch doctors ── */
  const fetchDoctors = useCallback(async (page, name, speciality) => {
    setDocsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: DOCS_PER_PAGE });
      if (name) params.set("name", name);
      if (speciality) params.set("speciality", speciality);
      const res = await makeRequest({ route: `/taleeb/doctor?${params}`, method: "get" });
      setDoctors(res?.data?.doctors?.data || []);
      const total = res?.data?.doctors?.total_items || 0;
      setTotalPages(Math.max(1, Math.ceil(total / DOCS_PER_PAGE)));
    } catch {
      toast.error("Failed to load doctors. Please try again.");
    } finally {
      setDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDoctors(1, "", "");
  }, [fetchDoctors]);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setSearchName(name);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchDoctors(1, name, searchSpeciality);
    }, 400);
  };

  const handleSpecialityChange = (e) => {
    const speciality = e.target.value;
    setSearchSpeciality(speciality);
    clearTimeout(searchTimerRef.current);
    setCurrentPage(1);
    fetchDoctors(1, searchName, speciality);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchDoctors(page, searchName, searchSpeciality);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectDoctor = (doc) => {
    setSelectedDoctor(doc);
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlot(null);
    setCurrentMonth(new Date());
    setStep("booking");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getDoctorImageUrl = (doctorId) => {
    const wd = websiteDocMap[String(doctorId)];
    if (wd?.hasImage) return `${BASE_URL}/website/doctor/${wd.id}/image`;
    return null;
  };

  /* ── Fetch slots ── */
  const fetchSlots = useCallback(
    async (date) => {
      if (!selectedDoctor) return;
      const dateStr = format(date, "yyyy-MM-dd");
      const startDate = encodeURIComponent(`${dateStr} 00:00:00`);
      const endDate = encodeURIComponent(`${dateStr} 23:59:59`);
      setSlotsLoading(true);
      setSlots([]);
      setSelectedSlot(null);
      try {
        const res = await makeRequest({
          route: `/taleeb/slots?doctorId=${selectedDoctor.DOCTOR_ID}&startDate=${startDate}&endDate=${endDate}`,
          method: "get",
        });
        setSlots(res?.data?.slots?.slots || []);
        if (!(res?.data?.slots?.slots || []).length) {
          toast.info("No available slots for this date. Try another day.");
        }
      } catch {
        toast.error("Failed to load slots. Please try another date.");
      } finally {
        setSlotsLoading(false);
        setTimeout(() => slotsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
      }
    },
    [selectedDoctor],
  );

  const today = startOfDay(new Date());
  const maxDate = addMonths(today, 3);

  const handleDateSelect = (date) => {
    if (isBefore(date, today)) return;
    setSelectedDate(date);
    fetchSlots(date);
  };

  /* ── Book ── */
  const handleBook = async () => {
    if (!selectedSlot) return;
    // If not logged in, open the identity modal instead of booking directly
    if (!patient?.mrno) {
      setShowPatientModal(true);
      return;
    }
    setBooking(true);
    try {
      await makeRequest({
        route: "/taleeb/appointment",
        method: "post",
        data: { mrno: patient.mrno, srNo: selectedSlot.SR_NO },
      });
      setBookedMrno(patient.mrno);
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.message || "Booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  /* ── Modal helpers ── */
  const resetModal = () => {
    setModalTab("existing");
    setModalMrno("");
    setModalOtpStep("mrno");
    setModalLookupResult(null);
    setModalSelectedPhone("");
    setModalOtp("");
    setModalOtpInfo(null);
    setModalOtpLoading(false);
    setModalOtpError("");
    setNewFirst("");
    setNewLast("");
    setNewPhone("");
    setNewCnic("");
    setNewDob("");
    setNewGender("1");
    setNewPatientStep("form");
    setNewTempMrno("");
    setNewOtp("");
    setNewOtpInfo(null);
    setNewRegLoading(false);
    setModalError("");
  };

  const handleMrnoLookup = async () => {
    if (!modalMrno.trim()) {
      setModalOtpError("Please enter your MR number.");
      return;
    }
    setModalOtpError("");
    setModalOtpLoading(true);
    try {
      const res = await patientApi.lookup({ mrno: modalMrno.trim() });
      setModalLookupResult(res);
      setModalOtpStep("select_phone");
    } catch (e) {
      setModalOtpError(e?.message || "Patient not found. Please check your MR number.");
    } finally {
      setModalOtpLoading(false);
    }
  };

  const handleSelectPhone = async (phone) => {
    setModalSelectedPhone(phone);
    setModalOtpError("");
    setModalOtpLoading(true);
    try {
      const res = await patientApi.requestOtp({ mrno: modalMrno.trim(), selectedPhone: phone });
      setModalOtpInfo({ maskedPhone: res.maskedPhone?.masked ?? res.maskedPhone, expiresInMinutes: res.expiresInMinutes });
      setModalOtpStep("otp_pending");
    } catch (e) {
      setModalOtpError(e?.message || "Failed to send OTP. Please try again.");
    } finally {
      setModalOtpLoading(false);
    }
  };

  const handleVerifyAndBook = async () => {
    if (!modalOtp.trim()) {
      setModalOtpError("Please enter the OTP.");
      return;
    }
    setModalOtpError("");
    setBooking(true);
    try {
      const authData = await patientApi.verifyOtp({ mrno: modalMrno.trim(), otp: modalOtp.trim() });
      login(authData);
      await makeRequest({ route: "/taleeb/appointment", method: "post", data: { mrno: modalMrno.trim(), srNo: selectedSlot.SR_NO } });
      setBookedMrno(modalMrno.trim());
      setShowPatientModal(false);
      resetModal();
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setModalOtpError(e?.message || "Verification failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  const resetNewPatient = () => {
    setNewPatientStep("form");
    setNewTempMrno("");
    setNewOtp("");
    setNewOtpInfo(null);
    setModalError("");
  };

  const handleNewPatientRegister = async () => {
    if (!newFirst.trim() || !newPhone.trim() || !newDob || !newGender) {
      setModalError("First name, phone number, date of birth and gender are required.");
      return;
    }
    setModalError("");
    setNewRegLoading(true);
    try {
      const res = await makeRequest({
        route: "/taleeb/temp-patient",
        method: "post",
        data: {
          firstName: newFirst.trim(),
          lastName: newLast.trim() || undefined,
          phoneNumber: newPhone.trim(),
          cnic: newCnic.trim() || undefined,
          dob: newDob,
          gender: newGender,
        },
      });
      const mrno = res?.data?.patient?.MRNO || res?.data?.patient?.mrno;
      if (!mrno) throw new Error("Could not register patient. Please try again.");
      setNewTempMrno(mrno);
      const normalizedPhone = "92" + newPhone.trim().replace(/\D/g, "").slice(-10);
      const otpRes = await patientApi.requestOtp({ mrno, selectedPhone: normalizedPhone });
      setNewOtpInfo({ maskedPhone: otpRes.maskedPhone?.masked ?? otpRes.maskedPhone, expiresInMinutes: otpRes.expiresInMinutes });
      setNewPatientStep("otp_pending");
    } catch (e) {
      setModalError(e?.message || "Registration failed. Please try again.");
    } finally {
      setNewRegLoading(false);
    }
  };

  const handleVerifyAndBookNew = async () => {
    if (!newOtp.trim()) {
      setModalError("Please enter the OTP.");
      return;
    }
    setModalError("");
    setBooking(true);
    try {
      await patientApi.verifyOtp({ mrno: newTempMrno, otp: newOtp.trim() });
      await makeRequest({ route: "/taleeb/appointment", method: "post", data: { mrno: newTempMrno, srNo: selectedSlot.SR_NO } });
      setBookedMrno(newTempMrno);
      setShowPatientModal(false);
      resetModal();
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setModalError(e?.message || "Verification or booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  /* ── Calendar days ── */
  const getCalendarDays = () => {
    const start = startOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end: endOfMonth(currentMonth) });
    return { days, startPad: getDay(start) };
  };

  /* ── Auth loading ── */
  if (authLoading) {
    return (
      <main>
        <Breadcrumb title="Request an Appointment" />
        <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="appt-spinner" />
        </div>
      </main>
    );
  }

  /* ── Step index ── */
  const stepIndex = step === "doctors" ? 0 : step === "booking" ? 1 : 2;
  const STEPS = [
    { label: "Select Doctor", icon: "fa-user-doctor" },
    { label: "Pick Date & Time", icon: "fa-calendar-check" },
    { label: "Confirmed", icon: "fa-circle-check" },
  ];

  /* ══════════════════════════════════════════════════════════════════ */
  return (
    <main>
      <Breadcrumb title="Request an Appointment" />

      <section style={{ background: "#f4f8fd", padding: "64px 0 80px" }}>
        <div className="container">
          {/* Step indicator */}
          <div className="appt-steps">
            {STEPS.map((s, i) => (
              <div key={s.label} className={`appt-step ${i === stepIndex ? "active" : i < stepIndex ? "done" : ""}`}>
                <div className="appt-step__dot">
                  {i < stepIndex ? (
                    <i className="fa-solid fa-check" style={{ fontSize: 16 }} />
                  ) : (
                    <i className={`fa-solid ${s.icon}`} style={{ fontSize: 16 }} />
                  )}
                </div>
                <span className="appt-step__label">{s.label}</span>
              </div>
            ))}
          </div>

          {/* ══ DOCTORS ══ */}
          {step === "doctors" && (
            <>
              <div style={{ textAlign: "center", marginBottom: 36 }}>
                <span className="appt-section-label">
                  <i className="fa-solid fa-stethoscope" /> Our Consultants
                </span>
                <h2 style={{ fontSize: "clamp(24px,3vw,38px)", fontWeight: 900, color: "#0f172a", margin: "14px 0 0" }}>Find Your Doctor</h2>
              </div>

              {/* Search */}
              <div className="appt-search">
                <div className="appt-search__row">
                  <div className="appt-search__field">
                    <label>Search by Name</label>
                    <input
                      className="appt-search__input"
                      type="text"
                      placeholder="e.g. Dr. Ahmed..."
                      value={searchName}
                      onChange={handleNameChange}
                    />
                  </div>
                  <div className="appt-search__field">
                    <label>Filter by Speciality</label>
                    <select className="appt-search__input" value={searchSpeciality} onChange={handleSpecialityChange}>
                      <option value="">All Specialities</option>
                      {specialities.map((sp) => (
                        <option key={sp} value={sp}>
                          {sp}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Doctors grid */}
              {docsLoading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <div className="appt-spinner" />
                  <p style={{ marginTop: 16, color: "#64748b", fontSize: 14 }}>Loading doctors...</p>
                </div>
              ) : doctors.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
                  <i className="fa-solid fa-user-doctor" style={{ fontSize: 48, color: "#c4d0db", marginBottom: 16, display: "block" }} />
                  <p>No doctors found. Try a different search.</p>
                </div>
              ) : (
                <div className="row g-4">
                  {doctors.map((doc) => {
                    const imgUrl = getDoctorImageUrl(doc.DOCTOR_ID);
                    const wd = websiteDocMap[String(doc.DOCTOR_ID)];
                    return (
                      <div key={doc.DOCTOR_ID} className="col-xl-4 col-md-6">
                        <div className="appt-doc-card">
                          <div className="appt-doc-card__photo">
                            <DoctorImage
                              src={imgUrl}
                              name={doc.NAME}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                            {doc.SPECIALITY && <span className="appt-doc-card__speciality-badge">{doc.SPECIALITY}</span>}
                          </div>
                          <div className="appt-doc-card__body">
                            <h4 className="appt-doc-card__name">{doc.NAME}</h4>
                            {wd?.designation && <p className="appt-doc-card__desig">{wd.designation}</p>}
                            <p className="appt-doc-card__spec">
                              <i className="fa-solid fa-stethoscope" style={{ color: "#1b74bb", fontSize: 12 }} />
                              {doc.SPECIALITY || "General Medicine"}
                            </p>
                            <button className="appt-doc-card__btn" onClick={() => handleSelectDoctor(doc)}>
                              <i className="fa-solid fa-calendar-plus" />
                              Book Appointment
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="appt-pagination">
                  <button className="appt-page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                    <i className="fa-solid fa-chevron-left" style={{ fontSize: 12 }} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "..." ? (
                        <span key={`ellipsis-${idx}`} style={{ padding: "0 4px", color: "#94a3b8", alignSelf: "center" }}>
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          className={`appt-page-btn ${item === currentPage ? "active" : ""}`}
                          onClick={() => handlePageChange(item)}
                        >
                          {item}
                        </button>
                      ),
                    )}
                  <button className="appt-page-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                    <i className="fa-solid fa-chevron-right" style={{ fontSize: 12 }} />
                  </button>
                </div>
              )}
            </>
          )}

          {/* ══ BOOKING: Calendar + Slots ══ */}
          {step === "booking" && selectedDoctor && (
            <>
              <button className="appt-back-btn" onClick={() => setStep("doctors")} style={{ marginBottom: 28 }}>
                <i className="fa-solid fa-arrow-left" />
                Back to Doctors
              </button>

              <div className="appt-booking-grid">
                {/* Left: summary + calendar */}
                <div>
                  {/* Doctor summary */}
                  <div className="appt-doc-summary">
                    <div className="appt-doc-summary__photo">
                      {getDoctorImageUrl(selectedDoctor.DOCTOR_ID) ? (
                        <DoctorImage
                          src={getDoctorImageUrl(selectedDoctor.DOCTOR_ID)}
                          name={selectedDoctor.NAME}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <i className="fa-solid fa-user-doctor" style={{ fontSize: 28, color: "#93b9d9" }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          textTransform: "uppercase",
                          color: "#1b74bb",
                          marginBottom: 4,
                        }}
                      >
                        Selected Doctor
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginBottom: 2 }}>{selectedDoctor.NAME}</div>
                      <div style={{ fontSize: 12, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
                        <i className="fa-solid fa-stethoscope" style={{ color: "#1b74bb", fontSize: 11 }} />
                        {selectedDoctor.SPECIALITY || "General Medicine"}
                      </div>
                    </div>
                  </div>

                  {/* Calendar */}
                  <div className="appt-calendar">
                    <div className="appt-calendar__header">
                      <button
                        className="appt-cal-nav"
                        onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
                        disabled={format(startOfMonth(currentMonth), "yyyy-MM") <= format(startOfMonth(today), "yyyy-MM")}
                      >
                        <i className="fa-solid fa-chevron-left" style={{ fontSize: 13 }} />
                      </button>
                      <h3>{format(currentMonth, "MMMM yyyy")}</h3>
                      <button
                        className="appt-cal-nav"
                        onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
                        disabled={format(startOfMonth(currentMonth), "yyyy-MM") >= format(startOfMonth(maxDate), "yyyy-MM")}
                      >
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: 13 }} />
                      </button>
                    </div>
                    <div className="appt-calendar__days-header">
                      {WEEK_DAYS.map((d) => (
                        <span key={d}>{d}</span>
                      ))}
                    </div>
                    <div className="appt-calendar__grid">
                      {(() => {
                        const { days, startPad } = getCalendarDays();
                        const cells = [];
                        for (let i = 0; i < startPad; i++) {
                          cells.push(<div key={`pad-${i}`} className="appt-cal-day empty" />);
                        }
                        days.forEach((day) => {
                          const isPast = isBefore(day, today);
                          const isSelected = selectedDate && isSameDay(day, selectedDate);
                          cells.push(
                            <button
                              key={day.toISOString()}
                              className={`appt-cal-day${isToday(day) ? " today" : ""}${isSelected ? " selected" : ""}`}
                              disabled={isPast}
                              onClick={() => handleDateSelect(day)}
                            >
                              {format(day, "d")}
                            </button>,
                          );
                        });
                        return cells;
                      })()}
                    </div>
                  </div>
                </div>

                {/* Right: slots */}
                <div ref={slotsRef}>
                  {!selectedDate ? (
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: 16,
                        padding: "48px 24px",
                        border: "1.5px dashed #c4d8f0",
                        textAlign: "center",
                      }}
                    >
                      <i className="fa-regular fa-calendar" style={{ fontSize: 48, color: "#93b9d9", marginBottom: 16, display: "block" }} />
                      <p style={{ color: "#64748b", fontSize: 15, fontWeight: 600, margin: 0 }}>
                        Select a date from the calendar to see available time slots.
                      </p>
                    </div>
                  ) : (
                    <div className="appt-slots-panel">
                      {/* Date header */}
                      <div
                        style={{
                          background: "linear-gradient(135deg,#f0f7ff,#e0eefb)",
                          borderRadius: 12,
                          padding: "14px 18px",
                          marginBottom: 20,
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          border: "1px solid #bee0ef",
                        }}
                      >
                        <i className="fa-solid fa-calendar-day" style={{ color: "#1b74bb", fontSize: 20 }} />
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                            Available Slots for
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{format(selectedDate, "EEEE, MMMM d, yyyy")}</div>
                        </div>
                      </div>

                      {slotsLoading ? (
                        <div style={{ textAlign: "center", padding: "32px 0" }}>
                          <div className="appt-spinner" />
                          <p style={{ marginTop: 12, color: "#64748b", fontSize: 13 }}>Checking availability...</p>
                        </div>
                      ) : slots.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "32px 0", color: "#64748b" }}>
                          <i
                            className="fa-solid fa-calendar-xmark"
                            style={{ fontSize: 40, color: "#c4d0db", marginBottom: 12, display: "block" }}
                          />
                          <p style={{ margin: 0 }}>No available slots for this date.</p>
                          <p style={{ fontSize: 13, marginTop: 4 }}>Please select another date.</p>
                        </div>
                      ) : (
                        <>
                          <h4>
                            <i className="fa-solid fa-clock" style={{ color: "#1b74bb", marginRight: 8 }} />
                            {slots.length} slot{slots.length !== 1 ? "s" : ""} available
                          </h4>
                          <div className="appt-slots-grid">
                            {slots.map((slot) => (
                              <button
                                key={slot.SR_NO}
                                className={`appt-slot-btn${selectedSlot?.SR_NO === slot.SR_NO ? " selected" : ""}`}
                                onClick={() => setSelectedSlot(slot)}
                              >
                                <i className="fa-regular fa-clock" style={{ fontSize: 12 }} />
                                {parseSlotTime(slot.TIME_SLOT)}
                              </button>
                            ))}
                          </div>

                          {selectedSlot && (
                            <div className="appt-confirm-bar">
                              <div>
                                <span>Booking appointment with</span>
                                <strong>
                                  {selectedDoctor.NAME} — {parseSlotTime(selectedSlot.TIME_SLOT)}
                                </strong>
                              </div>
                              <button className="appt-confirm-btn" onClick={handleBook} disabled={booking}>
                                {booking ? (
                                  <>
                                    <i className="fa-solid fa-spinner fa-spin" /> Booking...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa-solid fa-check" /> Confirm Appointment
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Patient info reminder */}
                      <div
                        style={{
                          marginTop: 20,
                          padding: "12px 16px",
                          background: "#f0f7ff",
                          borderRadius: 10,
                          border: "1px solid #c4d8f0",
                          fontSize: 13,
                          color: "#1b74bb",
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <i className="fa-solid fa-circle-info" style={{ flexShrink: 0 }} />
                        {patient ? (
                          <span>
                            Booking as <strong>{patient.firstName || patient.name || patient.mrno}</strong> (MR# {patient.mrno})
                          </span>
                        ) : (
                          <span>
                            Select a time slot and click <strong>Confirm Appointment</strong> — you'll identify yourself at that step.
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ══ SUCCESS ══ */}
          {step === "success" && (
            <div className="appt-success">
              <div className="appt-success__icon">
                <i className="fa-solid fa-circle-check" />
              </div>
              <h2>Appointment Confirmed!</h2>
              <p>
                Your appointment with <strong>{selectedDoctor?.NAME}</strong> has been successfully booked for{" "}
                <strong>{selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}</strong> at{" "}
                <strong>{selectedSlot && parseSlotTime(selectedSlot.TIME_SLOT)}</strong>.
                <br />
                <br />
                Please arrive 15 minutes early and bring your MR Card and any relevant medical reports.
              </p>

              {/* Temp-patient early arrival reminder */}
              {bookedMrno?.startsWith("M01TMP") && (
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1.5px solid #fcd34d",
                    borderRadius: 12,
                    padding: "16px 20px",
                    maxWidth: 480,
                    margin: "0 auto 28px",
                    fontSize: 14,
                    color: "#92400e",
                    textAlign: "left",
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: "#f59e0b", fontSize: 20, flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong style={{ display: "block", marginBottom: 4, color: "#78350f" }}>
                      First-time patient? Please arrive 1 hour early.
                    </strong>
                    As a new patient, you'll need to complete your registration at the hospital reception and pay for the consultation before
                    your appointment. Please bring a valid CNIC.
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  className="appt-back-btn"
                  onClick={() => {
                    setStep("doctors");
                    setSelectedDoctor(null);
                    setSelectedDate(null);
                    setSelectedSlot(null);
                    setSlots([]);
                    setBookedMrno(null);
                  }}
                >
                  <i className="fa-solid fa-plus" />
                  Book Another Appointment
                </button>
                {patient && (
                  <button
                    className="appt-back-btn"
                    style={{ background: "#1b74bb", color: "#fff", border: "1.5px solid #1b74bb" }}
                    onClick={() => navigate("/patient-profile")}
                  >
                    <i className="fa-solid fa-user" />
                    View My Profile
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ══ PATIENT IDENTITY MODAL ══ */}
      {showPatientModal && (
        <div
          className="appt-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPatientModal(false);
              resetModal();
            }
          }}
        >
          <div className="appt-modal">
            <div className="appt-modal__header">
              <h3>
                <i className="fa-solid fa-user-check" />
                Confirm Your Identity
              </h3>
              <button
                className="appt-modal__close"
                onClick={() => {
                  setShowPatientModal(false);
                  resetModal();
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="appt-modal__body">
              {/* Booking summary */}
              <div className="appt-modal__booking-summary">
                <strong>{selectedDoctor?.NAME}</strong>
                <span>
                  {selectedDate && format(selectedDate, "EEE, MMM d yyyy")} ·{" "}
                  <span style={{ color: "#1b74bb", fontWeight: 700 }}>{selectedSlot && parseSlotTime(selectedSlot.TIME_SLOT)}</span>
                </span>
              </div>

              {/* Tabs */}
              <div className="appt-modal__tabs">
                <button
                  className={`appt-modal__tab${modalTab === "existing" ? " active" : ""}`}
                  onClick={() => {
                    setModalTab("existing");
                    setModalOtpStep("mrno");
                    setModalOtpError("");
                    setModalError("");
                  }}
                >
                  <i className="fa-solid fa-id-card" style={{ marginRight: 6 }} />
                  Have MR #
                </button>
                <button
                  className={`appt-modal__tab${modalTab === "new" ? " active" : ""}`}
                  onClick={() => {
                    setModalTab("new");
                    resetNewPatient();
                    setModalError("");
                  }}
                >
                  <i className="fa-solid fa-user-plus" style={{ marginRight: 6 }} />
                  New Patient
                </button>
              </div>

              {/* ── Existing patient OTP flow ── */}
              {modalTab === "existing" && (
                <div>
                  {modalOtpStep === "mrno" && (
                    <>
                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <input
                          className="appt-modal__input"
                          style={{ flex: 1 }}
                          placeholder="Your MR Number (e.g. M0100000001234)"
                          value={modalMrno}
                          onChange={(e) => {
                            setModalMrno(e.target.value);
                            setModalOtpError("");
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleMrnoLookup()}
                        />
                        <button
                          onClick={handleMrnoLookup}
                          disabled={modalOtpLoading}
                          style={{
                            flexShrink: 0,
                            background: "#1b74bb",
                            color: "#fff",
                            border: "none",
                            borderRadius: 10,
                            padding: "11px 16px",
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: modalOtpLoading ? "not-allowed" : "pointer",
                            opacity: modalOtpLoading ? 0.6 : 1,
                          }}
                        >
                          {modalOtpLoading ? <i className="fa-solid fa-spinner fa-spin" /> : "Send OTP"}
                        </button>
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>An OTP will be sent to your registered phone number.</div>
                    </>
                  )}

                  {modalOtpStep === "select_phone" && modalLookupResult && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0d3b6e", marginBottom: 10 }}>
                        <i className="fa-regular fa-user" style={{ marginRight: 6, color: "#1b74bb" }} />
                        Welcome, {modalLookupResult.firstName || "Patient"} — select a number to receive OTP:
                      </div>
                      {(modalLookupResult.phoneNumbers || []).map((ph) => (
                        <button
                          key={ph.normalized}
                          className="appt-modal__phone-btn"
                          onClick={() => handleSelectPhone(ph.normalized)}
                          disabled={modalOtpLoading}
                        >
                          <i className="fa-solid fa-mobile-screen" style={{ fontSize: 12 }} />
                          {ph.masked}
                          {modalOtpLoading && modalSelectedPhone === ph.normalized && (
                            <i className="fa-solid fa-spinner fa-spin" style={{ marginLeft: "auto" }} />
                          )}
                        </button>
                      ))}
                      <button
                        className="appt-modal__link"
                        onClick={() => {
                          setModalOtpStep("mrno");
                          setModalOtpError("");
                        }}
                      >
                        ← Use a different MR number
                      </button>
                    </div>
                  )}

                  {modalOtpStep === "otp_pending" && modalOtpInfo && (
                    <div>
                      <div className="appt-modal__info">
                        <i className="fa-solid fa-message" style={{ marginRight: 7 }} />
                        OTP sent to <strong>{modalOtpInfo.maskedPhone}</strong>
                        {modalOtpInfo.expiresInMinutes ? ` · expires in ${modalOtpInfo.expiresInMinutes} min` : ""}
                      </div>
                      <input
                        className="appt-modal__input"
                        placeholder="Enter 6-digit OTP"
                        value={modalOtp}
                        onChange={(e) => {
                          setModalOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                          setModalOtpError("");
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleVerifyAndBook()}
                        maxLength={6}
                        inputMode="numeric"
                        style={{ letterSpacing: "0.2em", fontWeight: 700, fontSize: "1rem" }}
                        autoFocus
                      />
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12 }}>
                        <button
                          className="appt-modal__link"
                          style={{ color: "#1b74bb" }}
                          onClick={() => handleSelectPhone(modalSelectedPhone)}
                          disabled={modalOtpLoading}
                        >
                          {modalOtpLoading ? <i className="fa-solid fa-spinner fa-spin" /> : "Resend OTP"}
                        </button>
                        <button
                          className="appt-modal__link"
                          onClick={() => {
                            setModalOtpStep("mrno");
                            setModalOtpError("");
                          }}
                        >
                          ← Change number
                        </button>
                      </div>
                      <button className="appt-modal__btn" onClick={handleVerifyAndBook} disabled={booking || !modalOtp.trim()}>
                        {booking ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin" /> Verifying &amp; Booking…
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-shield-check" /> Verify OTP &amp; Confirm Booking
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {modalOtpError && (
                    <div className="appt-modal__error" style={{ marginTop: 10 }}>
                      <i className="fa-solid fa-circle-exclamation" />
                      {modalOtpError}
                    </div>
                  )}
                </div>
              )}

              {/* ── New patient form + OTP flow ── */}
              {modalTab === "new" && (
                <div>
                  {newPatientStep === "form" && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                        <div className="appt-modal__grid2">
                          <input
                            className="appt-modal__input"
                            placeholder="First Name *"
                            value={newFirst}
                            onChange={(e) => setNewFirst(e.target.value)}
                          />
                          <input
                            className="appt-modal__input"
                            placeholder="Last Name"
                            value={newLast}
                            onChange={(e) => setNewLast(e.target.value)}
                          />
                        </div>
                        <input
                          className="appt-modal__input"
                          placeholder="Phone Number *"
                          value={newPhone}
                          onChange={(e) => setNewPhone(e.target.value)}
                        />
                        <input
                          className="appt-modal__input"
                          placeholder="CNIC (optional)"
                          value={newCnic}
                          onChange={(e) => setNewCnic(e.target.value)}
                        />
                        <div className="appt-modal__grid2">
                          <input
                            className="appt-modal__input"
                            type="date"
                            value={newDob}
                            onChange={(e) => setNewDob(e.target.value)}
                            title="Date of Birth"
                          />
                          <select className="appt-modal__input" value={newGender} onChange={(e) => setNewGender(e.target.value)}>
                            <option value="1">Male</option>
                            <option value="2">Female</option>
                          </select>
                        </div>
                      </div>
                      {modalError && (
                        <div className="appt-modal__error" style={{ marginTop: 10 }}>
                          <i className="fa-solid fa-circle-exclamation" />
                          {modalError}
                        </div>
                      )}
                      <button className="appt-modal__btn" onClick={handleNewPatientRegister} disabled={newRegLoading}>
                        {newRegLoading ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin" /> Registering…
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-user-plus" /> Register &amp; Get OTP
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {newPatientStep === "otp_pending" && newOtpInfo && (
                    <div>
                      <div className="appt-modal__info">
                        <i className="fa-solid fa-message" style={{ marginRight: 7 }} />
                        OTP sent to <strong>{newOtpInfo.maskedPhone}</strong>
                        {newOtpInfo.expiresInMinutes ? ` · expires in ${newOtpInfo.expiresInMinutes} min` : ""}
                      </div>
                      <input
                        className="appt-modal__input"
                        placeholder="Enter 6-digit OTP"
                        value={newOtp}
                        onChange={(e) => {
                          setNewOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                          setModalError("");
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleVerifyAndBookNew()}
                        maxLength={6}
                        inputMode="numeric"
                        style={{ letterSpacing: "0.2em", fontWeight: 700, fontSize: "1rem" }}
                        autoFocus
                      />
                      <div style={{ marginTop: 8, marginBottom: 2 }}>
                        <button className="appt-modal__link" onClick={resetNewPatient}>
                          ← Change details
                        </button>
                      </div>
                      {modalError && (
                        <div className="appt-modal__error" style={{ marginTop: 10 }}>
                          <i className="fa-solid fa-circle-exclamation" />
                          {modalError}
                        </div>
                      )}
                      <button className="appt-modal__btn" onClick={handleVerifyAndBookNew} disabled={booking || !newOtp.trim()}>
                        {booking ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin" /> Verifying &amp; Booking…
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-shield-check" /> Verify OTP &amp; Confirm Booking
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AppointmentMain;
